import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { cpiSummary } from "@/lib/macro-report-summary";
import { getUsCpi } from "@/lib/cpi.functions";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-inflation-cpi";
const TITLE = "US CPI and Gold: Inflation Impact on XAUUSD | XAU Pro Academy";
const DESC =
  "Free US inflation tracker for gold traders. Headline and Core CPI from official BLS data, monthly trend, and how gold moved in each release month, explained simply.";

const FAQ = [
  { q: "Why does US CPI move gold?", a: "CPI shapes expectations for Federal Reserve policy. Hotter inflation can lift rate expectations and the US dollar, which often pressures gold on the release. Softer inflation tends to do the opposite. Over longer horizons, persistent inflation can support gold as a store of value." },
  { q: "What is the difference between Headline and Core CPI?", a: "Headline CPI includes all items. Core CPI excludes food and energy, which are volatile. Markets and the Federal Reserve usually weight Core more heavily when judging the underlying inflation trend." },
  { q: "When is CPI released?", a: "The Bureau of Labor Statistics publishes CPI monthly, usually around mid month at 08:30 New York time, covering the prior month." },
  { q: "Should I trade the CPI release directly?", a: "Release candles often sweep liquidity on both sides before direction is clear. A disciplined approach waits for the initial spike to complete, then requires structure, displacement and a defined invalidation before any execution." },
  { q: "Where does this data come from?", a: "CPI index values come from the official BLS public API (CPI-U, not seasonally adjusted). Year over year and month over month changes are computed from those index values. Gold monthly returns come from our XAUUSD monthly history." },
];

export const Route = createFileRoute("/gold-inflation-cpi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "US CPI gold, inflation and gold price, CPI XAUUSD, core CPI gold impact, gold inflation tracker, CPI release gold" },
      { property: "og:title", content: "US CPI and Gold: Inflation Impact Tracker" },
      { property: "og:description", content: "Official BLS inflation data with gold reaction per release month. Clear for beginners, useful for experienced traders." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "US CPI Inflation and Gold Monthly Reaction",
          description: "Monthly US Headline and Core CPI year over year and month over month changes with XAUUSD return in each release month.",
          url: `${SITE}${PATH}`,
          license: "https://xauproacademy.com/terms", isAccessibleForFree: true, inLanguage: "en", variableMeasured: ["Headline CPI y/y", "Core CPI y/y", "Headline CPI m/m", "Core CPI m/m", "XAUUSD release month return"], temporalCoverage: "2020-01-01/..", distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: `${SITE}${PATH}` }], dateModified: new Date().toISOString().slice(0, 10), creator: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          isBasedOn: "https://www.bls.gov/cpi/",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "US CPI and Gold", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  loader: () => getUsCpi(),
  staleTime: 60 * 60 * 1000,
  component: CpiPage,
});

const monthLabel = (p: string) => {
  const [y, m] = p.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};
const fp = (x: number | null, sign = false) => (x == null ? "n/a" : `${sign && x > 0 ? "+" : ""}${x.toFixed(1)}%`);


function CpiPage() {
  const data = Route.useLoaderData();
  const months = data.months;
  const latest = months[months.length - 1];
  const [sel, setSel] = useState(latest?.period ?? "");
  const idx = Math.max(0, months.findIndex((m) => m.period === sel));
  const cur = months[idx] ?? latest;
  const prev = months[idx - 1];
  const isHistorical = !!latest && cur?.period !== latest.period;
  const series = useMemo(() => months.slice(-36).map((m) => ({ ...m, label: monthLabel(m.period) })), [months]);
  const reactions = useMemo(() => months.filter((m) => m.goldReleaseMonthPct != null).slice(-24).map((m) => ({ ...m, label: monthLabel(m.period) })), [months]);

  const [vars, setVars] = useState<Record<string, string>>({});
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const out: Record<string, string> = {};
    for (const k of ["gold", "chart-silver", "border", "muted-foreground", "destructive", "card", "foreground", "muted"]) out[k] = cs.getPropertyValue(`--${k}`).trim();
    setVars(out);
  }, []);
  const c = (k: string) => vars[k] || "currentColor";

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container relative mx-auto max-w-6xl px-4 pb-14 pt-16 md:pt-24">
          <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            Institutional Tools · BLS Data
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            US CPI and Gold: <span className="text-gradient-gold">inflation impact</span> on XAUUSD
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Track US Headline and Core inflation from official data, see whether price pressure is rising or cooling, and
            review how gold behaved in each release month.
          </p>
          {latest && (
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Latest reference month {monthLabel(latest.period)} · Released monthly 08:30 ET · Source: BLS
            </p>
          )}
        </div>
      </section>

      {!latest ? (
        <section className="container mx-auto max-w-6xl px-4 py-16 text-center text-muted-foreground">{data.error ?? "Inflation data is temporarily unavailable."}</section>
      ) : (
        <>
          {data.stale && <p role="status" className="container mx-auto max-w-6xl px-4 pt-6 text-sm text-gold">The source is temporarily unavailable. Showing the last successfully retrieved data.</p>}
          <section className="sticky top-16 z-20 border-b border-border/60 bg-background/90 backdrop-blur">
            <div className="container mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
              <label className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="font-mono uppercase tracking-[0.18em]">CPI month</span>
                <select aria-label="CPI month" value={cur.period} onChange={(e) => setSel(e.target.value)} className="rounded-md border border-border bg-card px-3 py-2 font-mono text-sm text-foreground">
                  {months.slice(-12).reverse().map((m) => <option key={m.period} value={m.period}>{monthLabel(m.period)}{m.period === latest.period ? " (latest)" : ""}</option>)}
                </select>
              </label>
              {isHistorical ? (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gold">Viewing {monthLabel(cur.period)}</span>
                  <Button size="sm" variant="outline" onClick={() => setSel(latest.period)}>Latest print</Button>
                </div>
              ) : <span className="font-mono text-xs uppercase text-muted-foreground">Latest print</span>}
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 pt-10">
            <div className="rounded-xl border border-gold/30 bg-card p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">This month in 30 seconds · {monthLabel(cur.period)}</p>
              <dl className="mt-4 grid gap-4 md:grid-cols-2">
                {cpiSummary(cur, prev).map((l) => (
                  <div key={l.label}>
                    <dt className="text-sm font-semibold">{l.label}</dt>
                    <dd className="mt-1 text-sm text-muted-foreground">{l.text}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 text-xs text-muted-foreground">Context for bias only. Not a trade signal.</p>
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 py-10">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Headline CPI y/y" value={fp(cur.headlineYoY)} sub={prev ? `prior ${fp(prev.headlineYoY)}` : ""} />
              <Stat label="Core CPI y/y" value={fp(cur.coreYoY)} sub={prev ? `prior ${fp(prev.coreYoY)}` : ""} />
              <Stat label="Headline m/m (NSA)" value={fp(cur.headlineMoM, true)} sub={`Core ${fp(cur.coreMoM, true)}`} />
              <Stat label="Gold in release month" value={fp(cur.goldReleaseMonthPct, true)} sub={cur.goldReleaseMonthPct == null ? "Month in progress" : "XAUUSD open to close"} tone={cur.goldReleaseMonthPct == null ? "" : cur.goldReleaseMonthPct >= 0 ? "text-gold" : "text-destructive"} />
            </div>

            <div className="mt-10 rounded-xl border border-border bg-card p-5">
              <h2 className="font-display text-xl font-bold">Inflation trend, 3 years</h2>
              <p className="mt-1 text-sm text-muted-foreground">Year over year change. The dashed line marks the Federal Reserve 2% objective.</p>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={30} />
                    <YAxis tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} />
                    <Tooltip contentStyle={{ background: c("card"), border: `1px solid ${c("border")}` }} labelStyle={{ color: c("foreground") }} itemStyle={{ color: c("foreground") }} cursor={{ fill: c("muted"), fillOpacity: 0.25 }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                    <Legend />
                    <ReferenceLine y={2} stroke={c("muted-foreground")} strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="headlineYoY" name="Headline" stroke={c("gold")} dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="coreYoY" name="Core" stroke={c("chart-silver")} dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {reactions.length > 0 && (
              <div className="mt-8 rounded-xl border border-border bg-card p-5">
                <h2 className="font-display text-xl font-bold">Gold in each CPI release month</h2>
                <p className="mt-1 text-sm text-muted-foreground">Labelled by CPI reference month. The bar shows the XAUUSD monthly return of the following month, when that print was published.</p>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reactions}>
                      <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={30} />
                      <YAxis tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} />
                      <Tooltip contentStyle={{ background: c("card"), border: `1px solid ${c("border")}` }} labelStyle={{ color: c("foreground") }} itemStyle={{ color: c("foreground") }} cursor={{ fill: c("muted"), fillOpacity: 0.25 }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                      <ReferenceLine y={0} stroke={c("muted-foreground")} />
                      <Bar dataKey="goldReleaseMonthPct" name="Gold">
                        {reactions.map((r) => <Cell key={r.period} fill={(r.goldReleaseMonthPct ?? 0) >= 0 ? c("gold") : c("destructive")} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    {["Month", "Headline y/y", "Core y/y", "Headline m/m", "Core m/m", "Gold release month"].map((h) => <th key={h} className="px-4 py-3 text-left">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {months.slice(-12).reverse().map((m) => (
                    <tr key={m.period} className={`border-b border-border/50 ${m.period === cur.period ? "bg-gold-muted" : ""}`}>
                      <td className="px-4 py-2 font-mono">{monthLabel(m.period)}</td>
                      <td className="px-4 py-2">{fp(m.headlineYoY)}</td>
                      <td className="px-4 py-2">{fp(m.coreYoY)}</td>
                      <td className="px-4 py-2">{fp(m.headlineMoM, true)}</td>
                      <td className="px-4 py-2">{fp(m.coreMoM, true)}</td>
                      <td className={`px-4 py-2 ${m.goldReleaseMonthPct == null ? "" : m.goldReleaseMonthPct >= 0 ? "text-gold" : "text-destructive"}`}>{fp(m.goldReleaseMonthPct, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-10 rounded-xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-bold">Execution around the CPI release</h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                <li>The first minutes after 08:30 ET often sweep liquidity on both sides. Avoid committing during the initial spike.</li>
                <li>Wait for the release candle range to complete, then look for a body close beyond structure with displacement.</li>
                <li>Prefer entries from a fresh POI formed by the move, with invalidation beyond the swept liquidity.</li>
                <li>Spreads widen around the release. Size for the wider stop, or stand aside. Missing the move is acceptable.</li>
              </ul>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <Link to="/gold-cot-report" className="text-gold hover:underline">Gold COT Report</Link>
                <Link to="/gold-analysis" className="text-gold hover:underline">Daily gold analysis</Link>
                <Link to="/smart-money-concepts" className="text-gold hover:underline">Smart Money Concepts</Link>
                <Link to="/education" className="text-gold hover:underline">Education hub</Link>
              </div>
            </div>
          </section>
        </>
      )}

      <section className="container mx-auto max-w-4xl px-4 pb-16">
        <h2 className="font-display text-2xl font-bold">Frequently asked questions</h2>
        <div className="mt-4 space-y-4">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-lg border border-border bg-card p-4">
              <h3 className="font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value, sub, tone = "" }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`mt-2 font-display text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
